import type { ObjectFitType } from "@v1/types/object-fit";
import type { AssetLoadingType } from "replo-runtime/shared/asset-loading";

import * as React from "react";

import { selectActiveCanvas } from "@v1/components/canvas/canvas-reducer";
import ErrorMessage from "@v1/components/common/ErrorMessage/ErrorMessage";
import { BADGE_TRIGGER_OFFSET } from "@v1/components/editor/constants";
import ModifierGroup from "@v1/components/editor/page/element-editor/components/extras/ModifierGroup";
import ImageSourceSelector from "@v1/components/editor/page/element-editor/components/modifiers/ImageSourceSelector";
import { LengthInputSelector } from "@v1/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import {
  selectDraftComponentContext,
  selectDraftComponentId,
  selectImageSource,
  selectImageSourceComponentProps,
  selectObjectFit,
  selectObjectPosition,
  selectPropLoading,
  selectPropSrc,
} from "@v1/reducers/core-reducer";
import {
  resetEditorBrokenMediaComponentId,
  selectEditorBrokenMediaComponentIds,
  selectEditorMediaUploadingComponentIds,
} from "@v1/reducers/editor-media-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import { getImageSourceComponentActions } from "@v1/utils/getImageSourceComponentActions";
import { isImageSourceValid } from "@v1/utils/image";
import { styleAttributeToEditorData } from "@v1/utils/styleAttribute";
import { isDynamicDataValue } from "replo-runtime/shared/utils/dynamic-data";
import { evaluateVariableAsString } from "replo-runtime/store/ReploVariable";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import { coerceNumberToString } from "replo-utils/lib/misc";

import ModifierLabel from "../extras/ModifierLabel";

const ImageModifier = () => {
  const store = useEditorStore();
  const activeCanvas = useEditorSelector(selectActiveCanvas);
  const draftComponentId = useEditorSelector(selectDraftComponentId);
  const draftComponentContext = useEditorSelector(selectDraftComponentContext);
  const objectFit = useEditorSelector(selectObjectFit);
  const imageSource = useEditorSelector(selectImageSource);
  const rawObjectPosition = useEditorSelector(selectObjectPosition);
  const propSrc = useEditorSelector(selectPropSrc);
  const propLoading = useEditorSelector(selectPropLoading);
  const editorMediaUploadingComponentIds = useEditorSelector(
    selectEditorMediaUploadingComponentIds,
  );
  const applyComponentAction = useApplyComponentAction();
  const dispatch = useEditorDispatch();

  if (!draftComponentId) {
    return null;
  }

  const shouldOpenModifierWhenEmpty =
    !editorMediaUploadingComponentIds.includes(draftComponentId);

  const src = imageSource || propSrc;
  const isValidSrc = isMixedStyleValue(src) ? true : isImageSourceValid(src);

  const loading = propLoading ?? "eager";

  const objectXPosition = rawObjectPosition?.[0];
  const objectYPosition = rawObjectPosition?.[1];

  const onChangeImageSource = (value: string) => {
    const { imageComponentId, imageSourceProp, imageUrl } =
      selectImageSourceComponentProps(store.getState(), value);
    const actions = getImageSourceComponentActions({
      canvas: activeCanvas,
      imageSourceProp,
      imageUrl,
      targetComponentId: imageComponentId,
    });

    applyComponentAction({
      type: "applyCompositeAction",
      value: actions,
    });

    const isBroken = selectEditorBrokenMediaComponentIds(
      store.getState(),
    ).includes(draftComponentId);
    if (isBroken) {
      dispatch(resetEditorBrokenMediaComponentId(draftComponentId));
    }
  };

  const handleRemove = () => {
    const evaluatedVariable = isDynamicDataValue(imageSource ?? undefined)
      ? evaluateVariableAsString(
          imageSource as string,
          draftComponentContext ?? undefined,
        )
      : null;

    applyComponentAction({
      type: "applyCompositeAction",
      value: [
        {
          type: "setStyles",
          value: { __imageSource: evaluatedVariable ?? null },
        },
        {
          type: "deleteProps",
          propName: "src",
          analyticsExtras: { actionType: "edit", createdBy: "replo" },
        },
      ],
    });

    dispatch(resetEditorBrokenMediaComponentId(draftComponentId));
  };

  return (
    <ModifierGroup title="Image">
      <div className="flex w-full flex-col gap-2">
        <div className="flex flex-col gap-1">
          <div
            data-testid="image-source-modifier"
            className="flex items-center"
          >
            <ModifierLabel label="URL" />
            <ImageSourceSelector
              size="sm"
              src={src}
              objectFitValue={
                // Note (Noah, 2024-03-06): Cast is fine here since we never allow selecting any
                // object fit value other than those in ObjectFitType
                (objectFit ??
                  styleAttributeToEditorData.objectFit
                    .defaultValue) as ObjectFitType
              }
              assetLoadingValue={loading}
              objectPositionValues={{
                x: isMixedStyleValue(objectXPosition)
                  ? objectXPosition
                  : coerceNumberToString(objectXPosition) ?? undefined,
                y: isMixedStyleValue(objectYPosition)
                  ? objectYPosition
                  : coerceNumberToString(objectYPosition) ?? undefined,
              }}
              componentContext={draftComponentContext ?? undefined}
              onChangeImageSource={onChangeImageSource}
              onChangeObjectFit={(value: string) => {
                applyComponentAction({
                  type: "setStyles",
                  value: {
                    objectFit: value,
                  },
                });
              }}
              onChangeAssetLoading={(value: AssetLoadingType) => {
                applyComponentAction({
                  type: "setProps",
                  value: {
                    loading: value,
                  },
                });
              }}
              onChangeObjectPositionX={(value: string) => {
                applyComponentAction({
                  type: "setStyles",
                  value: {
                    objectPosition: `${value} ${objectYPosition ?? "center"}`,
                  },
                });
              }}
              onChangeObjectPositionY={(value: string) => {
                applyComponentAction({
                  type: "setStyles",
                  value: {
                    objectPosition: `${objectXPosition ?? "center"} ${value}`,
                  },
                });
              }}
              onRemove={handleRemove}
              componentId={draftComponentId ?? undefined}
              openPopoverWhenEmpty={shouldOpenModifierWhenEmpty}
              offset={BADGE_TRIGGER_OFFSET}
            />
          </div>
          {!isValidSrc ? (
            <ErrorMessage error="Please enter a valid image source" />
          ) : null}
        </div>
        <ZoomControl />
      </div>
    </ModifierGroup>
  );
};

const ZoomControl: React.FC = () => {
  const applyComponentAction = useApplyComponentAction();
  const zoomDefaultValue = styleAttributeToEditorData.__zoom.defaultValue;

  return (
    <div className="flex w-full">
      <LengthInputSelector
        label={<ModifierLabel label="Zoom" />}
        placeholder={zoomDefaultValue}
        anchorValue={zoomDefaultValue}
        resetValue={zoomDefaultValue}
        dragTrigger="label"
        metrics={["%"]}
        field="style.__zoom"
        onChange={(newValue) => {
          applyComponentAction({
            type: "setStyles",
            value: { __zoom: newValue },
          });
        }}
        className="w-full"
        menuOptions={["Reset", "100%", "110%", "120%"].map((option) => ({
          label: option,
          value: option === "Reset" ? zoomDefaultValue : option,
        }))}
        minValues={{ "%": 1 }}
        minDragValues={{ "%": 1 }}
      />
    </div>
  );
};

export default ImageModifier;
